import React, { useEffect, useState } from 'react';
import { Icon } from './inboxIcons';

// "Report a problem": anyone in Support flags something wrong. Opened from the chat header, the flag on a message or the
// order card (window event 'cx-report' with { where, type, commId, orderNumber, section, snapshot }). What the screen
// showed is saved with the report; admins and Support managers work them in Settings → Problems.
export const PROBLEM_TYPES = [
  ['wrong_message', 'Wrong message sent'],
  ['wrong_info', 'Wrong information'],
  ['broken', "Something didn't work"],
  ['no_answer', 'No good answer for this'],
  ['other', 'Other'],
];
const ABOUT = { message: 'about this message', order: 'about this order', chat: 'about this chat' };
const PLACEHOLDER = {
  wrong_message: 'What was wrong in it, and what should it have said?',
  wrong_info: 'What does the screen show, and what is actually true?',
  broken: 'What did you try, and what happened?',
  no_answer: 'What do customers ask that we have no good answer for?',
  other: "What's wrong?",
};

/** Open the report form from anywhere in Support. */
export function reportProblem(detail) {
  window.dispatchEvent(new CustomEvent('cx-report', { detail }));
}

export function FlagButton({ label, onClick, className = '' }) {
  return (
    <button type="button" className={`cx-flag ${className}`} onClick={(e) => { e.stopPropagation(); onClick(); }} aria-label={label} title={label}>
      <Icon name="flag" size={13} />
    </button>
  );
}

function ProblemReport({ api, ticketId, channel, toast }) {
  const [form, setForm] = useState(null); // { where, type, commId, orderNumber, section, snapshot, truth, waiting }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const open = (e) => { setErr(null); setForm({ where: 'chat', type: '', truth: '', waiting: false, snapshot: {}, ...(e.detail || {}) }); };
    window.addEventListener('cx-report', open);
    return () => window.removeEventListener('cx-report', open);
  }, []);
  // A different chat: the form was about the old one
  useEffect(() => { setForm(null); }, [ticketId]);

  if (!form) return null;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const send = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api('/api/v1/support/problems', 'POST', { ...form, ticketId, channel });
      toast(r.problem?.deskReportId ? 'Reported. Sent to the Order Desk too.' : 'Reported. You will see the reply under Your reports.');
      setForm(null);
      window.dispatchEvent(new CustomEvent('cx-reported'));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const s = form.snapshot || {};
  const saved = [
    form.where === 'message' && 'this message as it was sent',
    form.where === 'order' && `the order card as shown${form.orderNumber ? ` (#${form.orderNumber}${s.stage ? ` · ${s.stage}` : ''}${s.late ? ' · late' : ''})` : ''}`,
    'the chat status', 'you', 'the time',
  ].filter(Boolean).join(', ');

  return (
    <div className="cx-keys" onClick={(e) => e.target === e.currentTarget && setForm(null)} onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Escape') setForm(null); }}>
      <div className="cx-keys-panel cx-report" role="dialog" aria-modal="true" aria-labelledby="cx-report-h">
        <div className="cx-keys-h"><h2 id="cx-report-h">Report a problem <span className="cx-hint">{ABOUT[form.where] || ABOUT.chat}</span></h2><button className="cx-btn cx-btn-icon cx-btn-ghost" onClick={() => setForm(null)} aria-label="Close"><Icon name="x" /></button></div>
        <div className="cx-report-b">
          {form.where === 'message' && s.message && <div className="cx-report-quote">{s.message.length > 220 ? `${s.message.slice(0, 220)}…` : s.message}</div>}
          <fieldset className="cx-report-types">
            <legend>What's wrong?</legend>
            {PROBLEM_TYPES.map(([id, label]) => (
              <label key={id} className={`cx-report-type${form.type === id ? ' on' : ''}`}><input type="radio" name="cx-report-type" checked={form.type === id} onChange={() => set({ type: id })} />{label}</label>
            ))}
          </fieldset>
          <label className="cx-report-field">
            <span>{form.type === 'no_answer' ? 'What do customers ask?' : 'What should it be?'}</span>
            <textarea rows={3} maxLength={1000} value={form.truth} placeholder={PLACEHOLDER[form.type] || PLACEHOLDER.other} onChange={(e) => set({ truth: e.target.value })} autoFocus />
          </label>
          <label className="cx-report-wait"><input type="checkbox" checked={!!form.waiting} onChange={(e) => set({ waiting: e.target.checked })} /> Customer is waiting on this</label>
          <p className="cx-report-saved"><b>Saved with your report:</b> {saved}. No phone number or address.{form.type === 'wrong_info' && form.where === 'order' ? ' It also goes to the Order Desk, who fix order data.' : ''}</p>
          {err && <div className="cx-err cx-err-box" role="alert">{err}</div>}
          <div className="cx-report-actions">
            <button className="cx-btn" onClick={() => setForm(null)}>Cancel</button>
            <button className="cx-btn cx-btn-primary" onClick={send} disabled={busy || !form.type}>{busy ? 'Sending…' : 'Send report'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProblemReport;
